import { closeSnackbar, enqueueSnackbar } from '@ui/snackbar'
import { deleteRequest, postRequest, retryUntilSuccessOrFailure } from 'src/state/utils'
import { createEmptyModel } from 'src/utils/emptyModel'
import { uploadIFCFile } from './ifc'

interface CreateProjectInput extends Omit<ProjectData, 'status'> {
  model?: File
  ifc_file?: File
  start_mode?: 'ifc' | 'empty' | 'json'
  create_empty_model?: boolean
  storey_heights?: number[]
}

const DEFAULT_EMPTY_MODEL_STOREY_HEIGHTS = [2.95]

export const moveProjectToTrash: RequestTypeVariable = {
  getKey: (id: string) => ['moveProjectToTrash', id],
  request: async (id: string): Promise<void> => {
    await postRequest<void>({
      url: `/projects/${id}/trash`,
    })
  },
}

export const restoreProjectFromTrash: RequestTypeVariable = {
  getKey: (id: string) => ['restoreProjectFromTrash', id],
  request: async (id: string): Promise<ProjectData> => {
    const { data } = await postRequest<ProjectData>({
      url: `/projects/${id}/restore`,
    })

    return data as ProjectData
  },
}

export const deleteProjectPermanently: RequestTypeVariable = {
  getKey: (id: string) => ['deleteProjectPermanently', id],
  request: async (id: string): Promise<void> => {
    await deleteRequest<Promise<void>>({
      url: `/projects/${id}`,
    })
  },
}

export const createProject: RequestType = {
  key: ['createProject'],
  request: async ({
    model,
    ifc_file,
    start_mode,
    create_empty_model,
    ...data
  }: CreateProjectInput): Promise<ProjectData> => {
    const { data: projectData } = await postRequest<ProjectData>({
      url: '/projects',
      data,
    })

    return projectData as ProjectData
  },
}

export const createProjectModel: RequestType = {
  key: ['createProjectModel'],
  request: async (data: CreateProjectInput): Promise<ProjectData> => {
    const shouldCreateEmptyModel = data.start_mode === 'empty' || data.create_empty_model
    const shouldStartFromJson =
      data.start_mode === 'json' || (!data.start_mode && Boolean(data.model))
    const shouldStartFromIfc =
      data.start_mode === 'ifc' || (!data.start_mode && Boolean(data.ifc_file))

    if (shouldCreateEmptyModel) {
      // Empty model creation always uses a single default storey.
      const emptyModel = createEmptyModel(DEFAULT_EMPTY_MODEL_STOREY_HEIGHTS)
      await createPlanarModelFromJson.request(data?.guid, emptyModel)
      return data as ProjectData
    } else if (shouldStartFromJson && data.model) {
      // Create model from uploaded file
      await createPlanarModel.request(data?.guid, data.model)
      return data as ProjectData
    } else if (shouldStartFromIfc && data.ifc_file) {
      // Empty model creation always uses a single default storey.
      const emptyModel = createEmptyModel(DEFAULT_EMPTY_MODEL_STOREY_HEIGHTS)
      await createPlanarModelFromJson.request(data?.guid, emptyModel)
      const uploadResponse = await uploadIFCFile.request(data?.guid, data.ifc_file)
      const conversionSnackbarKey = enqueueSnackbar({
        variant: 'info',
        message: 'Projekt erstellt, Konvertierung der IFC Datei...',
      })
      try {
        // Wait for the conversion task (GLB + metadata extraction) so the project
        // only reports as created once the IFC metadata is available.
        await retryUntilSuccessOrFailure({
          url: `/task-status/${uploadResponse.task_id}`,
        })
      } finally {
        closeSnackbar(conversionSnackbarKey)
      }
      return data as ProjectData
    } else {
      throw new Error('A valid project start option and required upload are needed')
    }
  },
}
export const editProject: RequestTypeVariable = {
  getKey: (projectId: string) => ['editProject', projectId],
  request: async (projectId: string, data: ProjectData): Promise<ProjectData> => {
    const { data: result } = await postRequest<ProjectData>({
      url: `/projects/${projectId}`,
      data,
    })

    return result as ProjectData
  },
}

export const duplicateProject: RequestType = {
  key: ['duplicateProject'],
  request: async (id: string): Promise<ProjectData> => {
    const { data: result } = await postRequest<ProjectData>({
      url: `/projects/${id}/duplicate`,
    })
    return result as ProjectData
  },
}

export const createPlanarModel: RequestType = {
  key: ['createPlanarModel'],
  request: async (id: string, data: File): Promise<PlanarModel> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()

      reader.addEventListener('load', async event => {
        if (!event?.target) return

        const str = event.target.result || ''
        const json = JSON.parse(str as string)

        try {
          const { data } = await postRequest<Promise<PlanarModel>>({
            url: `/projects/${id}/planar-model`,
            data: json,
          })

          // @ts-ignore
          resolve(data)
        } catch (error) {
          reject(error)
        }
      })

      reader.readAsText(data)
    })
  },
}

export const createPlanarModelFromJson: RequestType = {
  key: ['createPlanarModelFromJson'],
  request: async (id: string, model: PlanarModel): Promise<PlanarModel> => {
    const { data } = await postRequest<Promise<PlanarModel>>({
      url: `/projects/${id}/planar-model`,
      data: model,
    })

    return data as unknown as PlanarModel
  },
}
